import { memo } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface LogoProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether to show the text alongside the logo */
  showText?: boolean;
  /** Whether to show the tagline */
  showTagline?: boolean;
  /** Where the logo links to (null for no link) */
  linkTo?: string | null;
  /** Additional className for the wrapper */
  className?: string;
  /** Dark mode (for light backgrounds like hero sections) */
  variant?: 'default' | 'light';
}

const sizeClasses = {
  sm: {
    image: 'h-8 w-auto',
    text: 'text-lg',
    tagline: 'text-[10px]',
  },
  md: {
    image: 'h-10 w-auto',
    text: 'text-xl',
    tagline: 'text-xs',
  },
  lg: {
    image: 'h-12 w-auto',
    text: 'text-2xl',
    tagline: 'text-xs',
  },
  xl: {
    image: 'h-16 w-auto',
    text: 'text-3xl',
    tagline: 'text-sm',
  },
};

function LogoComponent({
  size = 'md',
  showText = false,
  showTagline = false,
  linkTo = '/',
  className,
  variant = 'default',
}: LogoProps) {
  const sizes = sizeClasses[size];
  
  const content = (
    <div className={cn(
      'flex items-center gap-3 group',
      linkTo && 'cursor-pointer',
      className
    )}>
      {/* Logo Image */}
      <div className="relative transition-transform duration-200 group-hover:scale-105">
        <img
          src="/logo.jpg"
          alt="CDRS Logo"
          className={cn(
            sizes.image,
            'object-contain rounded-lg shadow-sm'
          )}
        />
      </div>
      
      {/* Text (hidden by default - logo has text embedded) */}
      {showText && (
        <div className="flex flex-col">
          <span className={cn(
            'font-bold tracking-tight',
            sizes.text,
            variant === 'default' ? 'text-foreground' : 'text-white'
          )}>
            CDRS
          </span>
          {showTagline && (
            <span className={cn(
              'tracking-wide',
              sizes.tagline,
              variant === 'default' ? 'text-muted-foreground' : 'text-white/70'
            )}>
              Child Development Reporting System
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg">
        {content}
      </Link>
    );
  }

  return content;
}

export const Logo = memo(LogoComponent);

// Icon-only version for small spaces (uses only the figure from the logo)
interface LogoIconProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function LogoIconComponent({ size = 'md', className }: LogoIconProps) {
  const sizeMap = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };
  
  return (
    <div className={cn(
      'rounded-xl bg-gradient-to-br from-[#6B9AC4] to-[#7FC8A8] flex items-center justify-center shadow-sm overflow-hidden',
      sizeMap[size],
      className
    )}>
      <img
        src="/logo.jpg"
        alt="CDRS"
        className="w-full h-full object-cover"
      />
    </div>
  );
}

export const LogoIcon = memo(LogoIconComponent);
